import { stripMoneyOf } from './moneyText';
import { PLAN_BUSY_TEXT } from './planBusy';

/**
 * Câu lỗi hiển thị cho một lượt gọi API — tách riêng (KHÔNG import antd) để unit-test được dưới jest của
 * react-scripts (antd v6 + react-router v7 không load được ở đó, xem CLAUDE.md).
 *
 * 🔴 Ca đắt nhất: **504 do gateway cắt ở 180 giây**. Đo thật trên dev: `POST /event-plans` → client nhận
 * `504 "The upstream server is timing out"` sau 180.06s, trong khi **server vẫn chạy tới cùng và tạo plan xong**.
 * Nếu UI báo "thất bại" thì người dùng bấm lại và sinh bản trùng (đo trên prod: 3 plan trong 5 phút). Vì vậy
 * 504/502/timeout phải nói đúng bản chất: *việc vẫn đang chạy ở server, đừng bấm lại*.
 */

/** Việc có thể vẫn đang chạy ở server dù client đã nhận lỗi? (gateway cắt / mất kết nối, KHÔNG phải lỗi nghiệp vụ) */
export function isServerStillWorking(err: any): boolean {
    const status = err?.response?.status;
    if (status === 504 || status === 502 || status === 408) return true;
    if (status) return false; // có status khác ⇒ server đã trả lời tử tế, không phải bị cắt
    const msg = String(err?.code || err?.message || '');
    return /timeout|ECONNABORTED|ETIMEDOUT|Network Error/i.test(msg);
}

/**
 * Lỗi 409 "món trong Bank đang được dùng" (xoá nhân vật/location/prop). Trả chi tiết để UI hỏi lại rồi gọi
 * với `force`. `null` = không phải loại lỗi này.
 *
 * 🔴 Vì sao không xoá thẳng: nhân vật có thể đang giữ digital character đã gán (tài nguyên khan, 50 slot) và
 * kéo theo các bộ trang phục; location có ảnh plate đã gen. Xoá là không hoàn lại.
 */
export function bankInUse(err: any): { name: string; usage: string[] } | null {
    const d = err?.response?.data;
    if (err?.response?.status !== 409 || !d?.inUse) return null;
    return { name: String(d.name || 'món này'), usage: Array.isArray(d.usage) ? d.usage.map(String) : [] };
}

/** Câu lỗi server. `restInterceptor.handleError` của admin-service trả `{ errorCode, messages: [..] }` — không có
 * `message` ⇒ trước 260922 E3-P1 mọi 400/409 rơi về câu axios "Request failed with status code 400". */
function serverMessage(err: any): string {
    const d = err?.response?.data;
    return String(d?.message || (Array.isArray(d?.messages) ? d.messages.filter(Boolean).join(' · ') : '') || '');
}

/**
 * Câu server trả về. Hai định dạng cùng tồn tại: `{ message }` (controller tự trả) và `{ errorCode, messages: [] }`
 * (lỗi ném qua `restInterceptor`, vd `ConflictError` — 260923 D2). Không đọc `messages[0]` thì 409 nghiệp vụ bị hiện
 * thành câu "việc đang chạy" sai nghĩa.
 */
export function serverMessageOf(err: any): string | undefined {
    return serverMessage(err) || undefined;
}

/**
 * Câu lỗi cho người dùng. `what` là tên việc vừa bấm ("tạo plan", "pass dialogue") để câu chữ có ngữ cảnh.
 * Ưu tiên câu do server trả (đã tiếng Việt, ví dụ "Ref chưa sẵn sàng — Thiếu DC: …").
 */
export function apiErrorText(err: any, what = 'Việc'): string {
    // 261030: câu lỗi hiển thị cho người dùng không kèm số tiền (BE đôi khi viết «≈ $x» trong message).
    return stripMoneyOf(apiErrorTextRaw(err, what));
}
function apiErrorTextRaw(err: any, what: string): string {
    if (isServerStillWorking(err)) {
        return `${what}: kết nối bị cắt sau ~3 phút (giới hạn gateway) nhưng việc VẪN đang chạy ở server — đợi và xem lại danh sách, ĐỪNG bấm lại kẻo tạo bản trùng.`;
    }
    const status = err?.response?.status;
    const errorCode = err?.response?.data?.errorCode || err?.response?.data?.code;
    if (errorCode === 'PLAN_BUSY') return PLAN_BUSY_TEXT;   // 261030 S6: không phải lỗi hệ thống — thao tác khác vừa đổi plan, thử lại
    if (errorCode === 'GENERATION_REQUIRED') return `${what}: plan 4 bước cần chọn cấu hình tạo video (engine và độ phân giải) trước khi tạo.`;
    if (errorCode === 'LAYOUT_FOUR_USE_STEP1_ROUTES') return `${what}: plan 4 bước phải dùng các nút của Bước 1, không dùng route của giao diện cũ.`;
    // 261030 S12c T5(a): BE trả 503 CÓ KIỂU khi tự lưu lại kết quả đã tạo gặp lỗi tạm — hiện đúng câu Việt của BE («kết quả vẫn được giữ… bấm «Chạy tiếp»»), không câu «máy chủ bận» chung.
    // 261030 S12c fix2 (F2): BE trả 503 CÓ KIỂU khi chưa đọc đủ dữ liệu để chọn đoạn chạy tiếp — hiện câu Việt của BE (nút «Chạy tiếp» vẫn bấm lại được).
    if (errorCode === 'STRICT_READ_UNKNOWN') return serverMessage(err) || `${what}: máy chưa đọc đủ dữ liệu để biết nên chạy tiếp từ đâu — bấm «Chạy tiếp» lại sau vài giây.`;
    if (errorCode === 'CANDIDATE_SAVE_TRANSIENT') return serverMessage(err) || `${what}: chưa lưu lại được kết quả đã tạo (lỗi mạng/lưu trữ tạm thời) — kết quả vẫn được giữ, bấm «Chạy tiếp» để thử lại.`;
    if (status === 503) return `${what}: máy chủ đang bận hoặc vừa khởi động lại — thử lại sau vài giây.`;
    const fromServer = serverMessage(err);
    if (status === 409) {
        // 409 = khoá việc-đang-chạy (câu có số phút) HOẶC ConflictError nghiệp vụ (260923: tách/gộp event dạng
        // chuỗi, plan lẫn v1/v2) — cả hai đều có câu tiếng Việt từ server, dùng nguyên văn.
        return String(fromServer || `${what}: việc này đang chạy rồi — chờ nó xong.`);
    }
    if (fromServer) return `${what}: ${fromServer}`;
    // 261030 S10 (H5-F9): không in `err.message` thô của axios («Request failed with status code 500») — nói bằng tiếng Việt, kèm mã HTTP để báo kỹ thuật.
    if (status) return `${what}: máy chủ trả lỗi ${status}${status >= 500 ? ' — thử lại sau ít phút; nếu lặp lại, báo người phụ trách kỹ thuật (kèm giờ và tên plan)' : ' — thử tải lại trang rồi làm lại'}.`;
    const raw = String(err?.message || '');
    return `${what}: ${/[àáảãạăắằẳẵặâấầẩẫậèéẻẽẹêếềểễệìíỉĩịòóỏõọôốồổỗộơớờởỡợùúủũụưứừửữựỳýỷỹỵđ]/i.test(raw) ? raw : 'lỗi không rõ'}`;
}
